import { mapValues } from 'lodash-es'

const monthSequence = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const monthName = (index: number) => monthSequence[index]

const dayName = (i: number) => dayNames[i]

const formatPeriodTime = (dateInst: Date, withSec = false) => {
  const addZero = (num: number) => num.toString().length < 2 ? '0' + num : num.toString()

  const h = dateInst.getHours()
  const hours = h % 12 || 12
  const period = h < 12 ? 'AM' : 'PM'
  let time = `${addZero(hours)}:${addZero(dateInst.getMinutes())}`

  if (withSec) {
    time += ':' + addZero(dateInst.getSeconds())
  }

  return time + period
}

export const convertToUTCTokens = (strDate: string) => {
  const [date, time] = strDate.trim().split('T')
  const timeItems = time.replace('-', ':').split(':')
  let dateItems = date.split('-')
  dateItems = [...dateItems, ...timeItems]
  const UTCTokens = dateItems.map(token => Number(token))
  UTCTokens[1] = UTCTokens[1] - 1

  return UTCTokens
}

const HOUR = 1000 * 60 * 60
export const DAY = HOUR * 24

export const leftToDeadline = (() => {
  const MONTH = DAY * 30
  const YEAR = 365 * DAY

  type MoreThenRemainder = (remainingTime: number, timeUnit: number, unitNumber: number, messagePostfix: string) => string
  const moreThenRemainder: MoreThenRemainder = (remainingTime, timeUnit, unitNumber, messagePostfix) => {
    while (remainingTime < unitNumber * timeUnit) {
      unitNumber -= 1
    }

    return `${unitNumber} ${messagePostfix}`
  }

  return (remainingTime: number) => {
    if (remainingTime <= 0) { return 'Сlosed' }
    if (remainingTime <= MONTH - DAY * 2) {
      if (remainingTime < HOUR) { return 'under an hour' }
      if (remainingTime >= HOUR && remainingTime < HOUR * 2) { return '1 hour' }
      if (remainingTime >= HOUR * 2 && remainingTime < DAY) { return moreThenRemainder(remainingTime, HOUR, 23, 'hours') }
      if (remainingTime >= DAY && remainingTime < DAY * 2) { return '1 day' }
      return moreThenRemainder(remainingTime, DAY, 28, 'days')
    } else {
      if (remainingTime > MONTH - DAY * 2 && remainingTime <= MONTH + MONTH / 2) { return '1 month' }
      if (remainingTime > YEAR) { return 'over a year' }

      let months = 1
      while (remainingTime > MONTH * months + MONTH / 2) {
        months += 1
      }

      return `${months} months`
    }
  }
})()

export const formatDeadlineTooltip = (date: Date) => {
  let formattedDate = ''
  formattedDate += dayName(date.getDay()) + ' '
  formattedDate += monthName(date.getMonth()) + ' '
  formattedDate += date.getDate() + ', '
  formattedDate += date.getFullYear() + ', '
  formattedDate += formatPeriodTime(date)

  return formattedDate
}

export const deadlineIn = (dateISO8601: string, unit = 'd') => {
  const dateTarget = new Date(dateISO8601)
  const dateCurrent = new Date()
  const diffSec = (dateTarget.getTime() - dateCurrent.getTime()) / 1000

  if (unit === 'd') {
    return diffSec / (3600 * 24)
  } else if (unit === 'h') {
    return diffSec / (3600)
  } else if (unit === 's') {
    return diffSec
  }
}

export const isDatePassed = (dateString: string) => {
  if (!dateString) { return }

  const date = new Date(dateString)
  if (date.toString().includes('Invalid Date')) { return }

  return Date.now() > date.getTime()
}

export const formatSubmissionDate = (date = '') => {
  if (!date) { return '' }

  if (!date.includes('T')) {
    const dateWithTimezone = new Date(date.split(' ').join('T'))
    return `${monthName(dateWithTimezone.getMonth())} ${dateWithTimezone.getDate()}, ${dateWithTimezone.getFullYear()}`
  }

  const dateParts = date.split(/[-T:+]/)
  const d = new Date(Date.UTC(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]), Number(dateParts[3]), Number(dateParts[4]), Number(dateParts[5].split('.')[0])))

  const timezoneOffset = (parseInt(dateParts[6], 10) || 0) * 60 + (parseInt(dateParts[7], 10) || 0)
  const adjustedDate = new Date(d.getTime() - (timezoneOffset * 60000)) // Convert minutes to milliseconds

  return `${monthName(adjustedDate.getMonth())} ${adjustedDate.getDate()}, ${adjustedDate.getFullYear()}`
}

const splitStringDate = (date, sequence = ['day', 'month', 'year'], delimiter = '/') => {
  const tokens = date.split(delimiter)
  return sequence.reduce((acc, item, i) => {
    acc[item] = Number(tokens[i])
    return acc
  }, {})
}

const formatDateString = (string, dateTokens) => {
  const tokenProcessors = {
    $month: month => monthSequence[month - 1],
    $monthLetterAbbr: month => monthSequence[month - 1].substring(0, 3),
    $day: day => day.toString(),
    $year: year => year.toString()
  }

  return string.match(/\$\w+/g).reduce((acc, templateToken) => {
    const tokenName = templateToken.split(/[A-Z]/g)[0].substring(1)
    const token = dateTokens[tokenName]
    const formattedToken = tokenProcessors[templateToken](token)
    return acc.replace(templateToken, formattedToken)
  }, string)
}

export const formatDayMonthYearString = (date, formattedString) => {
  const tokens = splitStringDate(date)
  return formatDateString(formattedString, tokens)
}

export const calculateAge = (dateOfBirth: string): string => {
  if (!dateOfBirth) { return '' }
  const [month, day, year] = dateOfBirth.split('/').map(Number)
  const today = new Date()

  const birthDate = new Date(year, month - 1, day)
  let age = today.getFullYear() - birthDate.getFullYear()

  if (today.getMonth() < birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() < birthDate.getDate())) {
    age--
  }

  return age.toString()
}

export const generateExpirationDate = (discountExpiresAt: string): string => {
  const regex = /(?<day>\d{2})\/(?<month>\d{2})\/(?<year>\d{4})/
  const dateComponents = discountExpiresAt.match(regex).groups
  const { day, month, year } = mapValues(dateComponents, Number)
  const newDate = new Date(year, month - 1, day)
  return newDate.toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function dateDiff(a: Date, b: Date) {
  let delta = (b.valueOf() - a.valueOf()) / 1000
  const days = Math.floor(delta / 86400)
  delta -= days * 86400
  const hours = Math.floor(delta / 3600) % 24
  delta -= hours * 3600
  const minutes = Math.floor(delta / 60) % 60
  delta -= minutes * 60
  const seconds = delta % 60
  return {
    days,
    hours,
    minutes,
    seconds
  }
}

export function addDays(date: Date, days: number) {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}
